/**
 * The sentences that leave this site.
 *
 * Going viral is the whole point of the project, and a share sheet is only as good as the text it
 * pre-fills — nobody rewrites a post before sending it, they either send what is offered or close
 * the sheet. So these seven strings are the product, not a label on it, and three rules hold them
 * together:
 *
 *   1. **The number is the news.** "OpenAI is down" is a shrug; "OpenAI is down and 4 812 of us
 *      are yelling MUST HAVE AI at a cartoon wall" is a link somebody clicks. Every template that
 *      can carry a live number carries it in the first clause.
 *   2. **A missing number shortens the sentence, it never appears.** The share button is reachable
 *      before `hello` lands, during a reconnect, and from a square whose siege ended while the
 *      sheet was open. A template that interpolates blindly posts `undefined clicks` to a public
 *      timeline, and unlike a rendering bug that one cannot be taken back. Every builder below is
 *      composed from clauses that are dropped when their number is absent, and `fixture`-driven
 *      tests assert the degraded forms as well as the full ones.
 *   3. **The provider is named plainly.** `OpenPlaza` is the joke on the page; a post that says
 *      `OpenPlaza is down` is a post nobody can search for and nobody can verify. The parody
 *      lives in the site, the share text names the company (CLAUDE.md standing rules — naming a
 *      company is not using its logo).
 *
 * Pure functions over a plain context object, no store import: the sheet is opened from six
 * different components and each of them already holds the numbers it wants to boast about.
 */

import type { ProviderId } from '@/net/transport'
import { PROVIDERS } from '@/providers'
import { humanDuration } from '@/time'
import { group } from '@/ui/Odometer'

/**
 * Where a shared link says it came from — the `s=` in the share URL.
 *
 * Not analytics in the tracking sense: nothing identifying is stored and there are no cookies
 * (CLAUDE.md). It is one letter in a query string that tells the server which OG variant a
 * crawler is about to ask for and tells the owner which network actually moves traffic, which is
 * the difference between guessing at the next share text and knowing.
 */
export type ShareSource = 'x' | 'rd' | 'bs' | 'cp'

/** The seven states worth telling somebody about. */
export type ShareKind =
  | 'siege'
  | 'breach'
  | 'allclear'
  | 'codexReset'
  | 'codexPage'
  | 'peaceful'
  | 'history'
  | 'incident'

/**
 * Everything a template may interpolate — and every number optional, because that is the honest
 * shape of the caller. A square renders its share button from the same store snapshot that can be
 * a reconnect away from having no numbers in it at all.
 */
export interface ShareContext {
  kind: ShareKind
  /** Which square's OG image the link should preview. `all` is the three-up card. */
  provider: ProviderId | 'all'
  /** Clicks: this siege for `siege`/`allclear`, the reset counter for `codexPage`. */
  clicks?: number | undefined
  /** Zombies at the wall right now (`siege`). */
  zombies?: number | undefined
  /** Breaches: this siege for `allclear`/`history`, the new total for `breach`. */
  breaches?: number | undefined
  /** How long the siege ran, seconds (`allclear`). */
  durationS?: number | undefined
  /** Clicks it took to earn the last Codex reset (`codexReset`). */
  clicksTaken?: number | undefined
  /** Days of begging before that reset landed (`codexReset`). */
  days?: number | undefined
  /** The incident's own title, in the provider's words (`incident`). */
  title?: string | undefined
  /** The company, named plainly, when the provider id is not the subject (`incident`). */
  who?: string | undefined
  /**
   * The page the link should point at (`incident`). Every other kind has a fixed path; an
   * incident's is its own, and building it here from the id would be a second copy of
   * `status/incidents.ts`'s rule.
   */
  path?: string | undefined
}

export interface ShareCopy {
  /** `navigator.share`'s title, and the Reddit submission title. Never carries the URL. */
  title: string
  /** The post body, ending in the URL. This is what gets pasted into a timeline. */
  text: string
  /** The link itself, so a caller can copy it without re-deriving the query string. */
  url: string
}

/** The label a post uses for a provider: the real company, plainly (see rule 3 above). */
export function providerLabel(id: ProviderId | 'all'): string {
  if (id === 'all') return 'AI'
  return PROVIDERS.find((provider) => provider.id === id)?.label ?? id
}

/**
 * A number, or nothing.
 *
 * `undefined`, `null`, `NaN` and a negative all mean "the page does not know", and all four have
 * to collapse to the same absence — `NaN` in particular, because it is what arithmetic on a
 * missing wire field produces and it renders as the word `NaN` rather than throwing.
 */
function num(value: number | null | undefined): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null
  const whole = Math.trunc(value)
  return whole < 0 ? null : whole
}

/** The same narrow no-break grouping the odometer uses, so a post reads like the page did. */
function n(value: number): string {
  return group(value)
}

function plural(value: number, one: string, many: string): string {
  return `${n(value)} ${value === 1 ? one : many}`
}

/**
 * Which page a context opens. Incident and history shares have their own pages; a reset-watch
 * share opens the card on home and carries a query marker that only the crawler metadata reads.
 */
function pathFor(context: ShareContext): string {
  // An incident carries its own page, which is the whole point of sharing one: the durable link
  // for an outage is that page and not the home square.
  if (context.kind === 'incident' && context.path !== undefined && context.path !== '') {
    return context.path
  }
  // The reset-watch share button lives on the home card, so its link opens that same card. The
  // `share=reset` marker only selects its crawler preview; React still renders the ordinary home
  // route for a person who follows it.
  if (context.kind === 'codexReset' || context.kind === 'codexPage') return '/'
  if (context.kind === 'history') return '/history'
  return '/'
}

/**
 * `https://host/?p=anthropic&s=cp` — the link that gets shared.
 *
 * `p` selects the town/provider image. Reset shares use `share=reset` and carry the click count as
 * a cache-buster, so a social network does not reuse the preview it fetched for an earlier count.
 */
export function shareUrl(context: ShareContext, source: ShareSource, origin: string): string {
  const base = origin.replace(/\/+$/, '')
  const resetClicks =
    typeof context.clicks === 'number' && Number.isFinite(context.clicks)
      ? `&c=${Math.max(0, Math.trunc(context.clicks))}`
      : ''
  const query =
    context.kind === 'codexReset' || context.kind === 'codexPage'
      ? `share=reset${resetClicks}&s=${source}`
      : `p=${context.provider}&s=${source}`
  return `${base}${pathFor(context)}?${query}`
}

/**
 * One context and one destination in, one post out.
 *
 * `source` is baked into the URL rather than added by the caller so the text and the link can
 * never disagree about where the click came from.
 */
export function shareCopy(
  context: ShareContext,
  source: ShareSource,
  origin: string = typeof location === 'undefined' ? '' : location.origin,
): ShareCopy {
  const url = shareUrl(context, source, origin)

  // The URL gets its room first, and the title gives way. One clause on this page is somebody
  // else's text of unbounded length — an incident title — and if anything is going to be cut by
  // a network's limit it has to be that, never the link. A post about an outage with the link
  // truncated off the end is a post that sends nobody anywhere (B25's rule, on this side).
  const body = bodyFor(context, POST_LIMIT - url.length - 1)
  return { title: titleFor(context), text: `${body} ${url}`.trim(), url }
}

/**
 * X's post length, and the tightest of the three networks the sheet offers.
 *
 * Measured against the whole post rather than against X's own t.co arithmetic (which counts any
 * URL as 23 characters): the same string is offered to Bluesky, whose limit is 300 and who counts
 * every character, and to the clipboard, which has no limit and no forgiveness.
 */
const POST_LIMIT = 280

function titleFor(context: ShareContext): string {
  const who = providerLabel(context.provider)
  switch (context.kind) {
    case 'siege':
      return `${who} is down — Must Have AI`
    case 'breach':
      return `The zombies breached ${who} — Must Have AI`
    case 'allclear':
      return `${who} is back — Must Have AI`
    case 'codexReset':
      return 'A Codex reset happened — Must Have AI'
    case 'codexPage':
      return 'MUST HAVE RESET — Must Have AI'
    case 'history':
      return 'AI outage history — Must Have AI'
    case 'peaceful':
      return 'Must Have AI'
    case 'incident':
      return `${context.who ?? who} incident: ${context.title ?? 'outage'} — Must Have AI`
  }
}

/**
 * The body, without the URL.
 *
 * Every branch is written as "the full sentence, then the shorter ones", and each shorter one is
 * a sentence a person would actually write — not the full template with a hole in it. A siege
 * with no click count is still worth posting; `… and  of us are yelling …` is not.
 */
function bodyFor(context: ShareContext, titleBudget = POST_LIMIT): string {
  const who = providerLabel(context.provider)
  const clicks = num(context.clicks)
  const zombies = num(context.zombies)
  const breaches = num(context.breaches)

  switch (context.kind) {
    case 'siege': {
      const lead =
        clicks === null
          ? `${who} is down and we are yelling MUST HAVE AI at a cartoon wall.`
          : `${who} is down and ${n(clicks)} of us are yelling MUST HAVE AI at a cartoon wall.`
      const wall = zombies === null ? '' : ` ${n(zombies)}/${ZOMBIE_CAP} zombies.`
      return `${lead}${wall} Come click:`
    }

    case 'breach': {
      // `bc` is the count *after* the breach, so it is the ordinal of the one being shared —
      // "Breach #1" is the first wall down this siege, which is exactly what the stamp said.
      const which = breaches === null || breaches === 0 ? '' : ` Breach #${n(breaches)}.`
      return `The zombies BREACHED ${who}.${which} This is fine.`
    }

    case 'allclear': {
      const bill = [
        clicks === null ? null : `${plural(clicks, 'click', 'clicks')}`,
        breaches === null ? null : `${plural(breaches, 'breach', 'breaches')}`,
        context.durationS === undefined || num(context.durationS) === null
          ? null
          : humanDuration(context.durationS),
      ].filter((part): part is string => part !== null)
      if (bill.length === 0) return `${who} is back. We did that. Probably.`
      return `${who} is back. It took ${list(bill)}. We did that. Probably.`
    }

    case 'codexReset': {
      const taken = num(context.clicksTaken)
      const days = num(context.days)
      if (taken === null && days === null) return 'OpenAI granted a Codex reset. The begging worked.'
      const parts = [
        taken === null ? null : `${plural(taken, 'click', 'clicks')}`,
        days === null ? null : `${plural(days, 'day', 'days')} of begging`,
      ].filter((part): part is string => part !== null)
      return `OpenAI granted a Codex reset after ${list(parts)}.`
    }

    case 'codexPage': {
      if (clicks === null) return 'People are asking OpenAI for a Codex reset. Join the queue:'
      // "people" reads as a headcount and the counter is clicks, which is a lie the site tells
      // everywhere else too — but not here, where the whole post is a claim about a number.
      return `${n(clicks)} MUST HAVE RESET clicks and counting. Join the queue:`
    }

    case 'history': {
      const parts = [
        clicks === null ? null : `${plural(clicks, 'click', 'clicks')}`,
        breaches === null ? null : `${plural(breaches, 'breach', 'breaches')}`,
      ].filter((part): part is string => part !== null)
      if (context.provider === 'all' && parts.length === 0) {
        return 'Every AI outage since this site started, with the zombie count:'
      }
      if (parts.length === 0) return `Biggest AI outage siege so far: ${who}.`
      return `Biggest AI outage siege so far: ${who}, ${parts.join(', ')}.`
    }

    case 'peaceful':
      return 'All three AI providers are up. Suspicious. Watch the zombies wait:'

    case 'incident': {
      // The one share text on this site that quotes somebody else's words, because those words are
      // what a thread is already talking about — the incident's own title is what people paste.
      const name = context.who ?? who
      const bill = [
        context.durationS === undefined || num(context.durationS) === null
          ? null
          : humanDuration(context.durationS),
        clicks === null ? null : `${n(clicks)} clicks yelled at the wall`,
      ].filter((part): part is string => part !== null)

      // Everything except the title, measured, so the title is cut to whatever is left. Providers
      // file incidents with titles like "Elevated errors and increased latency affecting a subset
      // of requests to the Assistants API in the us-east region", and one of those plus a URL is
      // over the limit before the sentence has said anything.
      const tail = bill.length === 0 ? '. The full report:' : ` — ${list(bill)}.`
      const frame = `${name} incident: ${tail}`
      const title = fit(context.title ?? 'outage', titleBudget - frame.length)

      return `${name} incident: ${title}${tail}`
    }
  }
}

/**
 * As much of somebody else's title as there is room for, cut on a word boundary.
 *
 * Never below a floor: a budget so small that the title would vanish means the rest of the sentence
 * is already too long, and a post reading `OpenAI incident: … — 4 h.` says less than one whose link
 * is a character over a limit that only one of the three networks enforces.
 */
function fit(title: string, budget: number): string {
  const room = Math.max(budget, MIN_TITLE)
  if (title.length <= room) return title
  const cut = title.slice(0, room - 1)
  const space = cut.lastIndexOf(' ')
  return `${(space > room / 2 ? cut.slice(0, space) : cut).trimEnd()}…`
}

/** Below this a truncated title stops being a title and becomes three words and a dot. */
const MIN_TITLE = 24

/** The wall's capacity, and the only place the share text says it (docs/api.md: breach at 21). */
const ZOMBIE_CAP = 20

/** `a, b and c` — an Oxford-comma-free list, because this is a sentence and not a table. */
function list(parts: readonly string[]): string {
  if (parts.length <= 1) return parts[0] ?? ''
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`
}

/** Where a post goes. The three networks the intent URLs below are documented for. */
export interface ShareTarget {
  id: 'x' | 'reddit' | 'bluesky'
  label: string
  source: ShareSource
  href(copy: ShareCopy): string
}

/**
 * Intent URLs, one per network (`docs/tasks/F20`).
 *
 * X and Bluesky take the whole post as `text`; X also takes `url` separately and appends it, so
 * the body handed to X must *not* already end in the link or the post carries it twice. Reddit
 * takes a title and a URL and no body at all — its submission form has no text field for a link
 * post — so the title is the one that has to stand on its own there.
 */
export const SHARE_TARGETS: readonly ShareTarget[] = [
  {
    id: 'x',
    label: 'X',
    source: 'x',
    href: (copy) =>
      `https://x.com/intent/post?text=${encodeURIComponent(withoutUrl(copy))}&url=${encodeURIComponent(copy.url)}`,
  },
  {
    id: 'reddit',
    label: 'Reddit',
    source: 'rd',
    href: (copy) =>
      `https://www.reddit.com/submit?url=${encodeURIComponent(copy.url)}&title=${encodeURIComponent(withoutUrl(copy))}`,
  },
  {
    id: 'bluesky',
    label: 'Bluesky',
    source: 'bs',
    href: (copy) => `https://bsky.app/intent/compose?text=${encodeURIComponent(copy.text)}`,
  },
]

/** The post with its trailing link removed, for the two networks that add one themselves. */
export function withoutUrl(copy: ShareCopy): string {
  return copy.text.endsWith(copy.url) ? copy.text.slice(0, -copy.url.length).trim() : copy.text
}
